import React, { useEffect, useState } from 'react';
import { addToShopifyCart, getPrice, preloadAllProducts } from '../services/productService';
import { trackPriceCalculated, trackAddToCart, trackConfigurationChange, trackStepTiming, trackSessionComplete, trackError } from '../services/analytics';

const SHADE_TYPES = {
  'pleated': { name: 'Pleated' },
  'valu-line': { name: 'Valu-Line' },
  'premium': { name: 'Premium' }
};

// Convert color name to URL-friendly format (handleize)
function handleize(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

// Generate image URL from color name
function getColorImageUrl(colorName) {
  const handleizedName = handleize(colorName);
  return `https://ervp-spa.s3.us-east-1.amazonaws.com/shade-builder/static/images/${handleizedName}.jpg`;
}

// Calculate delivery window dates
function calculateDeliveryWindow(rushOrder) {
  const today = new Date();
  const startDays = rushOrder ? 5 : 12;
  const endDays = rushOrder ? 8 : 15;
  
  const startDate = new Date(today);
  startDate.setDate(today.getDate() + startDays);
  
  const endDate = new Date(today);
  endDate.setDate(today.getDate() + endDays);
  
  // Format dates as "Month Day"
  const formatDate = (date) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  };
  
  return `${formatDate(startDate)} – ${formatDate(endDate)}`;
}

// Helper function to set cart attributes for tracking
const setCartAttributes = async (rushOrder = false) => {
  try {
    const deliveryWindow = calculateDeliveryWindow(rushOrder);
    
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          'Shade_Builder_App': 'Used',
          'Shade_Builder_Timestamp': new Date().toISOString(),
          'Delivery window': deliveryWindow
        }
      })
    });
    
    if (!response.ok) {
      console.error('Failed to set cart attributes:', response.status);
    }
  } catch (error) {
    console.error('Error setting cart attributes:', error);
  }
};

function OrderConfirmation({ state, onUpdate, showRushOrder = false }) {
  console.log('OrderConfirmation component mounted with state:', state);
  
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [cartError, setCartError] = useState(null);
  const [price, setPrice] = useState(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(true);
  const [rushOrderPrice, setRushOrderPrice] = useState(50);
  const [quantity, setQuantity] = useState(1);
  const [isRushPromoActive, setIsRushPromoActive] = useState(false);

  // Auto-check rush order when quantity >= 3 (only when rush order feature is enabled)
  useEffect(() => {
    if (!showRushOrder) return;
    
    // Check if promotion applies (3+ shades)
    const promoApplies = quantity >= 3;

    // Auto-check rush order if promotion applies
    if (promoApplies && !state.rushOrder) {
      onUpdate({ rushOrder: true });
    }
  }, [quantity, showRushOrder]);

  // Track promotion status separately
  useEffect(() => {
    const promoActive = quantity >= 3 && state.rushOrder;
    setIsRushPromoActive(promoActive);
  }, [quantity, state.rushOrder]);

  useEffect(() => {
    // Preload all products and set cart attributes on mount
    preloadAllProducts().then(async () => {
      setCartAttributes(state.rushOrder);
      // Load rush order price from config
      try {
        const response = await fetch('https://ervp-spa.s3.us-east-1.amazonaws.com/shade-builder/products.json');
        const config = await response.json();
        if (config.rushOrderProduct?.price) {
          setRushOrderPrice(config.rushOrderProduct.price);
        }
      } catch (error) {
        console.error('Failed to load rush order price:', error);
      }
    }).catch(error => {
      console.error('Failed to preload products:', error);
      // Still set cart attributes even if preload fails
      setCartAttributes(state.rushOrder);
    });
  }, []);

  useEffect(() => {
    // Calculate price when state changes
    const calculatePrice = async () => {
      console.log('OrderConfirmation: Starting price calculation');
      console.log('Current state:', state);
      setIsLoadingPrice(true);
      try {
        const calculatedPrice = await getPrice(state);
        console.log('Calculated price:', calculatedPrice);
        setPrice(calculatedPrice);
        
        // Track price calculation with promo info
        if (calculatedPrice) {
          const actualRushFee = state.rushOrder && quantity < 3 ? rushOrderPrice : 0;
          const totalPrice = parseFloat(calculatedPrice) * quantity + actualRushFee;
          trackPriceCalculated({
            basePrice: parseFloat(calculatedPrice),
            rushFee: actualRushFee,
            totalPrice: totalPrice,
            shadeType: state.selectedType,
            configuration: state.shadeConfiguration,
            width: state.dimensions.width,
            height: state.dimensions.height,
            promoApplied: quantity >= 3 && state.rushOrder,
            quantity: quantity
          });
        }
      } catch (error) {
        console.error('Error calculating price:', error);
        setPrice(null);
        trackError('price_calculation_error', error.message, 4);
      } finally {
        setIsLoadingPrice(false);
      }
    };

    console.log('Checking dimensions:', state.dimensions);
    if (state.dimensions.width && state.dimensions.height) {
      calculatePrice();
    } else {
      console.log('Dimensions not set, skipping price calculation');
      setIsLoadingPrice(false);
    }
  }, [state.selectedType, state.shadeConfiguration, state.dimensions, state.rushOrder, rushOrderPrice, quantity]);

  const handleAddToCart = async () => {
    setIsAddingToCart(true);
    setCartError(null);

    try {
      // Track timing for step 4
      trackStepTiming(4);
      
      // Update cart attributes with current delivery window
      await setCartAttributes(state.rushOrder);
      
      // Track add to cart event
      trackAddToCart({
        shadeType: state.selectedType,
        configuration: state.shadeConfiguration,
        width: state.dimensions.width,
        height: state.dimensions.height,
        colors: state.colors,
        rushOrder: state.rushOrder,
        variantId: 'custom_shade' // This would be the actual variant ID in production
      }, price, quantity);
      
      // Track session completion
      trackSessionComplete();
      
      await addToShopifyCart(state, quantity);
      
      // Redirect to cart
      window.location.href = '/cart';
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      setCartError(error.message || 'Failed to add item to cart. Please try again.');
      trackError('add_to_cart_error', error.message, 4);
    } finally {
      setIsAddingToCart(false);
    }
  };

  // Build product name
  const productName = `${SHADE_TYPES[state.selectedType].name} ${state.shadeConfiguration === 'day-night' ? 'Day & Night' : 'Night'} - ${state.dimensions.width}" W × ${state.dimensions.height}" H`;

  return (
    <div>
      {/* Promotional Banners */}
      {showRushOrder && quantity >= 3 && (
        <div className="tw-mb-4 tw-p-3 tw-bg-green-50 tw-border tw-border-green-300 tw-rounded-lg">
          <p className="tw-text-sm tw-font-medium tw-text-green-800">
            ✅ <strong>Promotion Applied:</strong> Rush order processing is FREE with 3+ shades!
            Your order will be expedited automatically at no extra charge.
          </p>
        </div>
      )}

      {showRushOrder && quantity === 2 && (
        <div className="tw-mb-4 tw-p-3 tw-bg-yellow-50 tw-border tw-border-yellow-300 tw-rounded-lg">
          <p className="tw-text-sm tw-font-medium tw-text-yellow-800">
            💡 <strong>Add 1 More Shade:</strong> Get FREE rush order processing (save $50)!
          </p>
        </div>
      )}

      {/* Order Summary */}
      <div className="tw-bg-gray-50 tw-rounded-lg tw-p-6 tw-mb-6">
        
        {/* Product Details */}
        <div className="tw-mb-4">
          <p className="tw-font-medium tw-text-lg">{productName}</p>
          <p className="tw-text-sm tw-text-green-700 tw-mt-1">
            <svg className="tw-inline tw-w-4 tw-h-4 tw-mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Includes {quantity} FREE mounting bracket kit{quantity > 1 ? 's' : ''}
          </p>
        </div>

        {/* Color Swatches - 3 wide */}
        <div className="tw-grid tw-grid-cols-3 tw-gap-3 tw-mb-4">
          {/* Top Rail */}
          <div className="tw-text-center">
            {state.colors.topRail && (
              <img 
                src={getColorImageUrl(state.colors.topRail)} 
                alt={state.colors.topRail}
                className="tw-w-full tw-aspect-square tw-rounded tw-border tw-border-gray-300 tw-object-cover tw-mb-1"
              />
            )}
            <p className="tw-text-xs tw-text-gray-600">Top Rail</p>
            <p className="tw-text-sm tw-font-medium">{state.colors.topRail || 'Not selected'}</p>
          </div>
          
          {/* Night Shade */}
          <div className="tw-text-center">
            {state.colors.nightShade && (
              <img 
                src={getColorImageUrl(state.colors.nightShade)} 
                alt={state.colors.nightShade}
                className="tw-w-full tw-aspect-square tw-rounded tw-border tw-border-gray-300 tw-object-cover tw-mb-1"
              />
            )}
            <p className="tw-text-xs tw-text-gray-600">Night Shade</p>
            <p className="tw-text-sm tw-font-medium">{state.colors.nightShade || 'Not selected'}</p>
          </div>
          
          {/* Day Shade */}
          {state.shadeConfiguration === 'day-night' && (
            <div className="tw-text-center">
              {state.colors.dayShade && (
                <img 
                  src={getColorImageUrl(state.colors.dayShade)} 
                  alt={state.colors.dayShade}
                  className="tw-w-full tw-aspect-square tw-rounded tw-border tw-border-gray-300 tw-object-cover tw-mb-1"
                />
              )}
              <p className="tw-text-xs tw-text-gray-600">Day Shade</p>
              <p className="tw-text-sm tw-font-medium">{state.colors.dayShade || 'Not selected'}</p>
            </div>
          )}
        </div>
        
        {/* Price Section */}
        <div className="tw-border-t tw-pt-4 tw-mt-4">
          {showRushOrder && state.rushOrder && (
            <div className="tw-flex tw-justify-between tw-mb-2">
              <span className={isRushPromoActive ? "tw-text-green-600" : "tw-text-orange-600"}>
                Rush Order (3-5 day manufacturing, <strong>FREE with 3+ shades</strong>):
              </span>
              <span className="tw-font-medium">
                  <span className="tw-text-orange-600">+${rushOrderPrice.toFixed(2)}</span>
              </span>
            </div>
          )}
          
          <div className="tw-flex tw-justify-between tw-items-center tw-text-lg">
            <span className="tw-font-semibold">Price Each:</span>
            {isLoadingPrice ? (
              <span className="tw-font-bold">Loading...</span>
            ) : price ? (
              <span className="tw-font-bold tw-text-xl">${price}</span>
            ) : (
              <span className="tw-text-gray-500">Price unavailable</span>
            )}
          </div>
        </div>
      </div>

      {/* Rush Order Option */}
      {showRushOrder && (
        <div className={`tw-mb-6 tw-border tw-rounded-lg tw-p-4 ${
          isRushPromoActive
            ? 'tw-border-green-300 tw-bg-green-50'
            : 'tw-border-gray-200 tw-bg-gray-50'
        }`}>
          <label className={`tw-flex tw-items-start ${
            isRushPromoActive ? 'tw-cursor-default' : 'tw-cursor-pointer'
          }`}>
            <input
              type="checkbox"
              checked={state.rushOrder}
              disabled={isRushPromoActive}
              onChange={(e) => {
                if (!isRushPromoActive) {
                  trackConfigurationChange('rush_order', state.rushOrder, e.target.checked, 4);
                  onUpdate({ rushOrder: e.target.checked });
                  // Update cart attributes with new delivery window
                  setCartAttributes(e.target.checked);
                }
              }}
              className={`tw-mt-1 tw-mr-3 tw-h-4 tw-w-4 tw-rounded focus:tw-ring-2 focus:tw-ring-brand-red ${
                isRushPromoActive
                  ? 'tw-text-green-600 tw-cursor-default'
                  : 'tw-text-brand-red'
              }`}
            />
            <div>
              <p className="tw-font-medium">
                Rush Order: {isRushPromoActive ? (
                  <>
                    <span className="tw-line-through tw-text-gray-400">${rushOrderPrice}</span>
                    <span className="tw-ml-2 tw-text-green-600 tw-font-bold">FREE (3+ Shades)</span>
                  </>
                ) : (
                  `$${rushOrderPrice}`
                )}
              </p>
              <p className="tw-text-sm tw-text-gray-600">
                3-5 day manufacturing instead of standard 12-15 days
                  <span className="tw-block tw-text-green-600 tw-font-medium tw-mt-1">
                    ✨ FREE with your order of 3+ shades!
                  </span>
              </p>
            </div>
          </label>
        </div>
      )}

      {/* Non-refundable Notice */}
      <div className="tw-mb-6 tw-border tw-border-orange-300 tw-bg-orange-50 tw-rounded-lg tw-p-4">
        <p className="tw-text-sm">
          <strong className="tw-text-orange-800">Important:</strong> This is a <strong>custom, non-refundable</strong> product.
        </p>
      </div>

      {/* Error Message */}
      {cartError && (
        <div className="tw-mb-4 tw-p-3 tw-bg-red-100 tw-border tw-border-red-400 tw-text-red-700 tw-rounded">
          {cartError}
        </div>
      )}

      {/* Quantity Selector */}
      <div className="tw-mb-4">
        <label htmlFor="quantity" className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
          Quantity
          {showRushOrder && quantity === 2 && (
            <span className="tw-ml-2 tw-text-sm tw-text-gray-500">
              (Add 1 more for free rush order!)
            </span>
          )}
        </label>
        <select
          id="quantity"
          value={quantity}
          onChange={(e) => setQuantity(parseInt(e.target.value))}
          className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-base focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-red focus:tw-border-transparent"
        >
          {[...Array(10)].map((_, i) => (
            <option key={i + 1} value={i + 1}>
              {i + 1}
              {showRushOrder && i + 1 === 3 && ' (Free rush order!)'}
            </option>
          ))}
        </select>
      </div>

      {/* Total Price Display */}
      {price && quantity > 1 && (
        <div className="tw-mb-4 tw-p-4 tw-bg-gray-50 tw-rounded-lg">
          <div className="tw-flex tw-justify-between tw-items-center">
            <span className="tw-text-lg tw-font-semibold">Total Price:</span>
            <span className="tw-text-2xl tw-font-bold">
              ${(parseFloat(price) * quantity).toFixed(2)}
            </span>
          </div>
          <p className="tw-text-sm tw-text-gray-600 tw-mt-1">
            {quantity} shade{quantity > 1 ? 's' : ''} at ${price} each
          </p>
          {showRushOrder && state.rushOrder && isRushPromoActive && (
            <p className="tw-text-sm tw-text-green-600 tw-font-medium tw-mt-1">
              ✨ Rush order ($50 value) FREE with 3+ shades - discount applied at checkout
            </p>
          )}
          {showRushOrder && state.rushOrder && !isRushPromoActive && (
            <p className="tw-text-sm tw-text-orange-600 tw-mt-1">
              + Rush order fee ($50) will be added at checkout
            </p>
          )}
          <p className="tw-text-sm tw-text-green-700 tw-mt-1">
            + {quantity} FREE bracket kit{quantity > 1 ? 's' : ''} included
          </p>
        </div>
      )}

      {/* Add to Cart Button */}
      <button
        onClick={handleAddToCart}
        disabled={isAddingToCart || !price}
        className={`tw-w-full tw-py-3 tw-px-6 tw-rounded-md tw-font-semibold tw-text-lg tw-transition-colors
          ${!isAddingToCart && price
            ? 'tw-bg-brand-red tw-text-white hover:tw-bg-red-700'
            : 'tw-bg-gray-300 tw-text-gray-500 tw-cursor-not-allowed'}`}
      >
        {isAddingToCart ? 'Adding to Cart...' : 'Add to Cart'}
      </button>

    </div>
  );
}

export default OrderConfirmation;